import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../config/api";
import {
  Card,
  Space,
  Typography,
  Descriptions,
  Spin,
  Button,
  Tag
} from "antd";
import { useParams, Link, useNavigate } from "react-router-dom";
import { formatPolishCurrency } from "../../helpers/currency";
import { isLogged } from "../../helpers/account";
import { Helmet } from "react-helmet-async";
import {
  CalendarOutlined,
  EuroOutlined,
  ClockCircleOutlined,
  ArrowLeftOutlined,
  UserOutlined
} from "@ant-design/icons";
import { formatPolishDate } from "../../helpers/date";
import NewInquiryResponseForm from "../Inquiry/Forms/NewInquiryResponseForm";
import MarketplaceInquiryResponsesList from "./MarketplaceInquiryResponsesList";
import AvatarDisplay from "../Dashboard/Account/AvatarDisplay";

const { Title, Text } = Typography;

const MarketplaceInquiryPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState({});
  const { inquiryUuid } = useParams();
  const navigate = useNavigate();

  const fetchProductData = async () => {
    try {
      const productResponse = await fetch(
        `${api.host}/inquiries/${inquiryUuid}/public`,
        {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
          }),
        }
      );
      const productData = await productResponse.json();
      setProduct(productData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching product data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductData();
  }, [inquiryUuid]);

  return (
    <>
      <Helmet>
        <title>{product.name || t('inquiry.page.task')} - Taskizy</title>
      </Helmet>
      
      <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "20px" }}>
        <Spin spinning={loading}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            {/* Header */}
            <div style={{ marginBottom: "24px" }}>
              <div style={{ 
              display: "flex", 
              alignItems: window.innerWidth < 768 ? "flex-start" : "center", 
              flexDirection: window.innerWidth < 768 ? "column" : "row",
              gap: window.innerWidth < 768 ? "8px" : "16px", 
              marginBottom: "16px" 
            }}>
                <Link to="/marketplace/inquiries">
                  <Button icon={<ArrowLeftOutlined />} type="text">
                    {t('marketplace.inquiry.backToMarketplace')}
                  </Button>
                </Link>
                <Title level={2} style={{ margin: 0, color: "#2c3e50" }}>
                  {product.name}
                </Title>
              </div>
            </div>

            {/* Main Content */}
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "2fr 1fr", 
              gap: "24px" 
            }}>
              {/* Left Column - Details */}
              <div>
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  {/* Client Info */}
                  {product.account && (
                    <Card 
                      title={t('marketplace.inquiry.client')}
                      style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <AvatarDisplay src={product.account.photo} size={48} account={product.account} />
                        <div>
                          <Text strong style={{ fontSize: "16px" }}>
                            {product.account.fullname}
                          </Text>
                          <div style={{ color: "#6c757d", fontSize: "14px" }}>
                            {product.account.company_name || t('marketplace.inquiry.privatePerson')}
                          </div>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* Task Details */}
                  <Card 
                    title={t('inquiry.page.taskDetails')}
                    style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                  >
                    <Descriptions layout="vertical" column={1}>
                      <Descriptions.Item
                        label={
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <EuroOutlined />
                            <span>{t('inquiry.form.budget')}</span>
                          </div>
                        }
                      >
                        <Text strong style={{ fontSize: "18px", color: "#667eea" }}>
                          {formatPolishCurrency(product.budget)}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ClockCircleOutlined />
                            <span>{t('inquiry.page.executionTime')}</span>
                          </div>
                        }
                      >
                        <Text>{product.delivery_days} dni</Text>
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <CalendarOutlined />
                            <span>{t('inquiry.page.deadline')}</span>
                          </div>
                        }
                      >
                        <Text>{formatPolishDate(product.valid_to)}</Text>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>

                  {/* Task Description */}
                  <Card 
                    title={t('inquiry.page.taskDescription')}
                    style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                  >
                    <div
                      style={{ 
                        lineHeight: "1.6",
                        fontSize: "16px",
                        whiteSpace: "pre-wrap"
                      }}
                      dangerouslySetInnerHTML={{
                        __html: product.message
                          ? product.message.replace(/\n/g, "<br>")
                          : t('inquiry.page.noDescription'),
                      }}
                    />
                  </Card>

                  {/* Contact Info for non-escrow tasks */}
                  {!product.is_escrow_enabled && (
                    <Card 
                      title={t('marketplace.inquiry.contactInfo')}
                      style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                    >
                      <Descriptions layout="vertical" column={1}>
                        <Descriptions.Item label={t('marketplace.inquiry.contactEmail')}>
                          {isLogged() ? (
                            product.public_email || t('marketplace.inquiry.notProvided')
                          ) : (
                            <Link to="/transactions">{t('marketplace.inquiry.loggedUsersOnly')}</Link>
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('marketplace.inquiry.moreInfo')}>
                          {isLogged() ? (
                            product.public_url || t('marketplace.inquiry.notProvided')
                          ) : (
                            <Link to="/transactions">{t('marketplace.inquiry.loggedUsersOnly')}</Link>
                          )}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  )}

                  {/* Responses for escrow tasks */}
                  {product.is_escrow_enabled && (
                    <Card 
                      title={t('marketplace.inquiry.contractorOffers')}
                      style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                    >
                      <MarketplaceInquiryResponsesList inquiryUuid={product.uuid} />
                    </Card>
                  )}
                </Space>
              </div>

              {/* Right Column - Sidebar */}
              <div>
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  {/* Action Card */}
                  <Card 
                    title={t('inquiry.form.submitOffer')}
                    style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                  >
                    {product.is_escrow_enabled ? (
                      <>
                        {!isLogged() ? (
                          <div style={{ textAlign: "center" }}>
                            <Text style={{ display: "block", marginBottom: "16px", color: "#6c757d" }}>
                              {t('marketplace.inquiry.loginToSubmitOffer')}
                            </Text>
                            <Button
                              type="primary"
                              size="large"
                              onClick={() => navigate("/transactions")}
                              style={{ width: "100%" }}
                            >
                              {t('nav.login')}
                            </Button>
                          </div>
                        ) : (
                          <NewInquiryResponseForm inquiryUuid={product.uuid} />
                        )}
                      </>
                    ) : (
                      <div style={{ textAlign: "center" }}>
                        <Text style={{ display: "block", marginBottom: "16px", color: "#6c757d" }}>
                          {t('marketplace.inquiry.contactDirectly')}
                        </Text>
                        <Text style={{ fontSize: "14px", color: "#6c757d" }}>
                          {t('marketplace.inquiry.seeContactInfo')}
                        </Text>
                      </div>
                    )}
                  </Card>

                  {/* Quick Stats */}
                  <Card 
                    title={t('inquiry.page.summary')}
                    style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Text>{t('marketplace.inquiry.taskType')}:</Text>
                        <Tag color={product.is_escrow_enabled ? "success" : "default"}>
                          {product.is_escrow_enabled ? t('marketplace.inquiry.withEscrow') : t('marketplace.inquiry.directContact')}
                        </Tag>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Text>{t('inquiry.form.budget')}:</Text>
                        <Text strong style={{ color: "#667eea" }}>
                          {formatPolishCurrency(product.budget)}
                        </Text>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Text>{t('inquiry.page.executionTime')}:</Text>
                        <Text>{product.delivery_days} dni</Text>
                      </div>
                    </Space>
                  </Card>

                  {/* Help Card */}
                  <Card 
                    title={`💡 ${t('profile.tip')}`}
                    style={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.08)", background: "#f8f9fa" }}
                  >
                    <Text style={{ color: "#6c757d", fontSize: "14px" }}>
                      {product.is_escrow_enabled 
                        ? t('marketplace.inquiry.escrowTip')
                        : t('marketplace.inquiry.directTip')
                      }
                    </Text>
                  </Card>
                </Space>
              </div>
            </div>
          </div>
        </Spin>
      </div>
    </>
  );
};

export default MarketplaceInquiryPage;